import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ajouterProduit, uploaderImage } from "../../utils/productService";
import { CATEGORY_MAP } from "../../utils/categorymapping";

/**
 * ============================================================
 * GUIDE — CLÉS LOCALES ET ENDPOINTS
 * ============================================================
 *
 * - Les catégories classiques et cave sont séparées.
 * - Comme ça, une catégorie cave ("Alcool fort") ne pollue plus
 *   les catégories du magasin / produits classiques.
 *
 * Si ton endpoint cave a un autre nom, change uniquement
 * CAVE_CREATE_ENDPOINT ci-dessous.
 */
const LS_KEYS = {
  classic: "mdx_dynamic_categories_v1",
  cave: "mdx_dynamic_categories_cave_v1",
};

const CAVE_CREATE_ENDPOINT =
  "https://marchedeluxe.ch/services/ajouter_produit_cave.php";

const PRODUCT_RECO_SAVE_ENDPOINT =
  "https://marchedeluxe.ch/services/save_product_recommendations.php";

const normalizeCat = (v) =>
  String(v || "")
    .trim()
    .replace(/\s+/g, " ");

const getScopeKey = (scope) =>
  scope === "cave" ? LS_KEYS.cave : LS_KEYS.classic;

const loadDynamicCats = (scope = "classic") => {
  try {
    const raw = localStorage.getItem(getScopeKey(scope));
    const arr = JSON.parse(raw || "[]");
    return Array.isArray(arr) ? arr.map(normalizeCat).filter(Boolean) : [];
  } catch {
    return [];
  }
};

const saveDynamicCat = (cat, scope = "classic") => {
  const c = normalizeCat(cat);
  if (!c) return;

  const base = new Set(loadDynamicCats(scope));
  base.add(c);

  try {
    localStorage.setItem(getScopeKey(scope), JSON.stringify(Array.from(base)));
  } catch {
    // ignore
  }
};

/**
 * ============================================================
 * GUIDE — DÉTECTION PRODUIT LAITIER / FROMAGE
 * ============================================================
 *
 * Sert uniquement à savoir si on doit masquer les champs épicerie.
 * Le lait animal est géré séparément dans AdminMilkTypes.
 */
const isDairyCategory = (value) => {
  const c = String(value || "")
    .trim()
    .toLowerCase();

  return (
    c.includes("fromage") ||
    c.includes("vache") ||
    c.includes("brebis") ||
    c.includes("chèvre") ||
    c.includes("chevre") ||
    c.includes("lait")
  );
};

/**
 * ============================================================
 * GUIDE — HELPERS PRIX
 * ============================================================
 */
const toCents = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
};

/**
 * ============================================================
 * GUIDE — NORMALISATION SLOT RECOMMANDATION
 * ============================================================
 *
 * Format attendu :
 * - classic:294
 * - cave:301
 *
 * On enlève les espaces parasites pour fiabiliser la saisie admin.
 */
const normalizeRecoSlot = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

/**
 * ============================================================
 * GUIDE — PARSING SLOT RECOMMANDATION
 * ============================================================
 *
 * Retour :
 * - null si le champ est vide ou invalide
 * - { scope, product_id } si la saisie est valide
 */
const parseRecoSlot = (value) => {
  const raw = normalizeRecoSlot(value);
  if (!raw) return null;

  const match = raw.match(/^(classic|cave):(\d+)$/i);
  if (!match) return null;

  const scope = match[1].toLowerCase();
  const productId = Number(match[2] || 0);

  if (!productId) return null;

  return {
    scope,
    product_id: productId,
  };
};

/**
 * ============================================================
 * GUIDE — AJOUT PRODUIT SELON LE SCOPE
 * ============================================================
 *
 * - scope "classic" => ancien service existant
 * - scope "cave"    => endpoint cave dédié
 */
async function createProductByScope(scope, payload) {
  if (scope !== "cave") {
    return ajouterProduit(payload);
  }

  const res = await fetch(CAVE_CREATE_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...payload,
      product_scope: "cave",
      isCave: 1,
    }),
  });

  const data = await res.json();

  if (!res.ok || !data?.success) {
    throw new Error(
      data?.error || data?.message || "Impossible d'ajouter le produit cave."
    );
  }

  return data?.id || data?.product_id || null;
}

/**
 * ============================================================
 * GUIDE — SAUVEGARDE DES RECOMMANDATIONS PRODUIT
 * ============================================================
 *
 * Cette V1 admin sauvegarde jusqu’à 2 recommandations par produit.
 * La logique métier reste dans la table de relation dédiée,
 * ce qui évite de modifier le schéma principal des produits.
 */
async function saveProductRecommendations({
  sourceProductId,
  sourceScope,
  slot1,
  slot2,
}) {
  const parsed = [parseRecoSlot(slot1), parseRecoSlot(slot2)].filter(Boolean);

  const res = await fetch(PRODUCT_RECO_SAVE_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      source_product_id: sourceProductId,
      source_scope: sourceScope,
      recommendations: parsed,
    }),
  });

  const data = await res.json();

  if (!res.ok || !data?.success) {
    throw new Error(
      data?.message ||
        data?.error ||
        "Impossible de sauvegarder les produits recommandés."
    );
  }
}

export default function AddProductModal({
  showAddModal,
  setShowAddModal,
  onSaved,
  /**
   * ============================================================
   * GUIDE — productScope
   * ============================================================
   *
   * - "classic" => produit normal
   * - "cave"    => produit cave
   *
   * Depuis le dashboard :
   * - Ajouter produit classique => productScope="classic"
   * - Ajouter produit cave      => productScope="cave"
   */
  productScope = "classic",
}) {
  const isCaveMode = productScope === "cave";

  const [formP, setFormP] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dynamicCats, setDynamicCats] = useState([]);

  /**
   * ============================================================
   * GUIDE — recharge les catégories du bon scope à l'ouverture
   * ============================================================
   *
   * On initialise aussi les 2 slots de recommandations.
   */
  useEffect(() => {
    if (!showAddModal) return;

    setDynamicCats(loadDynamicCats(productScope));

    setFormP({
      __productScope: productScope,
      __isCave: isCaveMode,
      isOnline: 0,
      isDiscount: 0,
      stock: "",
      unite: "KG",
      name: "",
      category: "",
      newCategoryName: "",
      histoire: "",
      price: 0,
      old_price: 0,
      imageFile: null,

      /**
       * ========================================================
       * GUIDE — CHAMPS ÉPICERIE / CLASSIQUE NON LAITIER
       * ========================================================
       */
      origine: "",
      texture: "",
      intensite: "",

      /**
       * ========================================================
       * GUIDE — CHAMPS CAVE
       * ========================================================
       */
      millesime: "",
      region: "",
      cepage: "",

      recommendationSlot1: "",
      recommendationSlot2: "",
    });
  }, [showAddModal, productScope, isCaveMode]);

  /**
   * ============================================================
   * GUIDE — catégories affichées
   * ============================================================
   *
   * CLASSIC :
   * - CATEGORY_MAP
   * - catégories dynamiques classiques
   *
   * CAVE :
   * - seulement catégories dynamiques cave
   * - + catégorie en cours si besoin
   *
   * => ainsi les catégories cave n'apparaissent plus
   *    dans les pages classiques.
   */
  const categories = useMemo(() => {
    const fromMap = isCaveMode
      ? []
      : Object.keys(CATEGORY_MAP || {}).map(normalizeCat);

    const dyn = (dynamicCats || []).map(normalizeCat);

    const current =
      formP?.category && formP.category !== "NEW"
        ? [normalizeCat(formP.category)]
        : [];

    const extraNew = formP?.newCategoryName
      ? [normalizeCat(formP.newCategoryName)]
      : [];

    const all = new Set([...fromMap, ...dyn, ...current, ...extraNew]);

    return Array.from(all)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, "fr"));
  }, [dynamicCats, formP?.category, formP?.newCategoryName, isCaveMode]);

  /**
   * ============================================================
   * GUIDE — catégorie finale courante
   * ============================================================
   *
   * Sert à afficher les champs métiers conditionnels avant sauvegarde.
   */
  const currentFinalCategory = useMemo(() => {
    return formP.category === "NEW" ? formP.newCategoryName : formP.category;
  }, [formP.category, formP.newCategoryName]);

  /**
   * ============================================================
   * GUIDE — affichage champs épicerie
   * ============================================================
   *
   * Visible uniquement pour les produits classiques non laitiers.
   */
  const showEpicerieFields = useMemo(() => {
    if (isCaveMode) return false;
    return !isDairyCategory(currentFinalCategory);
  }, [currentFinalCategory, isCaveMode]);

  /**
   * ============================================================
   * GUIDE — fermeture modale
   * ============================================================
   */
  const closeModal = useCallback(() => {
    if (isSubmitting) return;
    setShowAddModal(false);
    setFormP({});
  }, [isSubmitting, setShowAddModal]);

  /**
   * ============================================================
   * GUIDE — sauvegarde
   * ============================================================
   *
   * En mode cave :
   * - catégorie sauvegardée dans le store cave
   * - ajout envoyé au backend cave
   *
   * En mode classic :
   * - comportement historique conservé
   *
   * Puis :
   * - upload image éventuel
   * - sauvegarde des 2 recommandations produit
   */
  const handleAddProduct = useCallback(async () => {
    const finalCategory =
      formP.category === "NEW" ? formP.newCategoryName : formP.category;

    const cat = normalizeCat(finalCategory);
    const name = String(formP.name || "").trim();

    if (!cat || !name) {
      alert("Veuillez remplir le nom et la catégorie du produit.");
      return;
    }

    const confirmLabel = isCaveMode ? "produit cave" : "produit";
    if (
      !window.confirm(
        `Confirmez-vous l'ajout de ce ${confirmLabel} "${name}" ?`
      )
    ) {
      return;
    }

    setIsSubmitting(true);

    try {
      /**
       * 1) persistance catégorie dans le bon bucket
       */
      saveDynamicCat(cat, productScope);
      setDynamicCats(loadDynamicCats(productScope));

      /**
       * 2) payload unifié produit
       */
      const payload = {
        ...formP,
        name,
        category: cat,
        stock: Number(formP.stock ?? 0) || 0,
        unite: formP.unite || "KG",
        isOnline: formP.isOnline === 1 ? 1 : 0,
        isDiscount: formP.isDiscount === 1 ? 1 : 0,
        price: Number(formP.price ?? 0) || 0,
        old_price: Number(formP.old_price ?? 0) || 0,
        histoire: formP.histoire || "",

        /**
         * ======================================================
         * GUIDE — PAYLOAD ÉPICERIE / CLASSIQUE
         * ======================================================
         */
        origine: showEpicerieFields ? formP.origine || "" : formP.origine || "",
        texture: showEpicerieFields ? formP.texture || "" : formP.texture || "",
        intensite: showEpicerieFields
          ? formP.intensite || ""
          : formP.intensite || "",

        /**
         * ======================================================
         * GUIDE — PAYLOAD CAVE
         * ======================================================
         */
        millesime: isCaveMode ? formP.millesime || "" : "",
        region: isCaveMode ? formP.region || "" : "",
        cepage: isCaveMode ? formP.cepage || "" : "",

        __productScope: productScope,
        __isCave: isCaveMode,
        product_scope: isCaveMode ? "cave" : "classic",
      };

      /**
       * 3) création via le bon backend
       */
      const productId = await createProductByScope(productScope, payload);

      /**
       * 4) upload image si fourni
       */
      if (productId && formP.imageFile) {
        await uploaderImage(productId, formP.imageFile);
      }

      /**
       * 5) sauvegarde des produits recommandés
       */
      if (productId) {
        await saveProductRecommendations({
          sourceProductId: productId,
          sourceScope: isCaveMode ? "cave" : "classic",
          slot1: formP.recommendationSlot1,
          slot2: formP.recommendationSlot2,
        });
      }

      setShowAddModal(false);
      setFormP({});
      onSaved?.();
    } catch (err) {
      console.error("Erreur lors de l'ajout :", err);
      alert(
        isCaveMode
          ? "Impossible d'ajouter le produit cave. Vérifiez l'endpoint cave et la sauvegarde des recommandations."
          : "Impossible d'ajouter le produit. Vérifiez la console."
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formP,
    isCaveMode,
    onSaved,
    productScope,
    setShowAddModal,
    showEpicerieFields,
  ]);

  if (!showAddModal) return null;

  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div
        className="modal-content admin-modal"
        style={{ maxWidth: "500px", width: "90%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>{isCaveMode ? "🍷 Nouveau Produit Cave" : "📦 Nouveau Produit"}</h2>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            textAlign: "left",
          }}
        >
          <label style={{ fontSize: "12px", fontWeight: "bold" }}>
            Nom du produit
          </label>
          <input
            type="text"
            placeholder={isCaveMode ? "Ex: Whisky 12 ans" : "Ex: Roquefort AOP"}
            className="search-input"
            value={formP.name || ""}
            onChange={(e) => setFormP({ ...formP, name: e.target.value })}
          />

          <label style={{ fontSize: "12px", fontWeight: "bold" }}>
            Catégorie
          </label>
          <select
            className="search-input"
            value={formP.category || ""}
            onChange={(e) =>
              setFormP({
                ...formP,
                category: e.target.value,
              })
            }
          >
            <option value="">-- Sélectionner --</option>

            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}

            <option value="NEW">➕ + Nouvelle catégorie...</option>
          </select>

          {formP.category === "NEW" && (
            <input
              type="text"
              placeholder="Nom de la nouvelle catégorie"
              className="search-input"
              style={{ border: "2px solid #d4af37" }}
              value={formP.newCategoryName || ""}
              onChange={(e) =>
                setFormP({
                  ...formP,
                  newCategoryName: e.target.value,
                })
              }
            />
          )}

          {isCaveMode && (
            <>
              <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                Millésime
              </label>
              <input
                type="text"
                placeholder="Ex: 2022"
                className="search-input"
                value={formP.millesime || ""}
                onChange={(e) =>
                  setFormP({ ...formP, millesime: e.target.value })
                }
              />

              <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                Région
              </label>
              <input
                type="text"
                placeholder="Ex: Valais"
                className="search-input"
                value={formP.region || ""}
                onChange={(e) =>
                  setFormP({ ...formP, region: e.target.value })
                }
              />

              <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                Cépage
              </label>
              <input
                type="text"
                placeholder="Ex: Pinot noir"
                className="search-input"
                value={formP.cepage || ""}
                onChange={(e) =>
                  setFormP({ ...formP, cepage: e.target.value })
                }
              />
            </>
          )}

          {showEpicerieFields && (
            <>
              <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                Origine
              </label>
              <input
                type="text"
                placeholder="Ex: Italie"
                className="search-input"
                value={formP.origine || ""}
                onChange={(e) =>
                  setFormP({ ...formP, origine: e.target.value })
                }
              />

              <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                Texture
              </label>
              <input
                type="text"
                placeholder="Ex: Croquant"
                className="search-input"
                value={formP.texture || ""}
                onChange={(e) =>
                  setFormP({ ...formP, texture: e.target.value })
                }
              />

              <label style={{ fontSize: "12px", fontWeight: "bold" }}>
                Intensité
              </label>
              <input
                type="text"
                placeholder="Ex: Doux"
                className="search-input"
                value={formP.intensite || ""}
                onChange={(e) =>
                  setFormP({ ...formP, intensite: e.target.value })
                }
              />
            </>
          )}

          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "12px" }}>Prix de base (CHF)</label>
              <input
                type="number"
                placeholder="ex: 12.50"
                className="search-input"
                onChange={(e) =>
                  setFormP({ ...formP, old_price: toCents(e.target.value) })
                }
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "12px" }}>Prix Promo (CHF)</label>
              <input
                type="number"
                placeholder="ex: 10.00"
                className="search-input"
                onChange={(e) =>
                  setFormP({ ...formP, price: toCents(e.target.value) })
                }
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "12px" }}>Stock</label>
              <input
                type="number"
                className="search-input"
                value={formP.stock || ""}
                onChange={(e) => setFormP({ ...formP, stock: e.target.value })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "12px" }}>Unité</label>
              <input
                type="text"
                placeholder="KG ou PIECE"
                className="search-input"
                value={formP.unite || "KG"}
                onChange={(e) => setFormP({ ...formP, unite: e.target.value })}
              />
            </div>
          </div>

          <label style={{ fontSize: "12px" }}>Histoire du produit</label>
          <textarea
            className="search-input"
            style={{ height: "60px" }}
            value={formP.histoire || ""}
            onChange={(e) => setFormP({ ...formP, histoire: e.target.value })}
          />

          <label style={{ fontSize: "12px", fontWeight: "bold" }}>
            Produits recommandés
          </label>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "10px",
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: "8px",
              background: "#faf8f3",
            }}
          >
            <div style={{ fontSize: "12px", lineHeight: 1.5, color: "#5c5247" }}>
              Format attendu : <strong>classic:294</strong> ou{" "}
              <strong>cave:301</strong>
            </div>

            <input
              type="text"
              placeholder="Produit recommandé #1 ex: classic:294"
              className="search-input"
              value={formP.recommendationSlot1 || ""}
              onChange={(e) =>
                setFormP({ ...formP, recommendationSlot1: e.target.value })
              }
            />

            <input
              type="text"
              placeholder="Produit recommandé #2 ex: cave:301"
              className="search-input"
              value={formP.recommendationSlot2 || ""}
              onChange={(e) =>
                setFormP({ ...formP, recommendationSlot2: e.target.value })
              }
            />
          </div>

          <label style={{ fontSize: "12px" }}>Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              setFormP({ ...formP, imageFile: e.target.files?.[0] })
            }
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              marginTop: "10px",
            }}
          >
            <label style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={formP.isOnline === 1}
                onChange={(e) =>
                  setFormP({ ...formP, isOnline: e.target.checked ? 1 : 0 })
                }
              />{" "}
              En ligne
            </label>

            <label style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={formP.isDiscount === 1}
                onChange={(e) =>
                  setFormP({ ...formP, isDiscount: e.target.checked ? 1 : 0 })
                }
              />{" "}
              Promo
            </label>
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
          <button
            onClick={handleAddProduct}
            className="add-btn"
            disabled={isSubmitting}
            type="button"
          >
            {isSubmitting ? "Envoi..." : "Confirmer l'ajout"}
          </button>

          <button
            onClick={closeModal}
            className="btn-luxury-outline"
            disabled={isSubmitting}
            type="button"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}